import { NavLink, Outlet, useLocation } from "react-router-dom";
import { isMeetingOnScreen } from "@/lib/patient-links";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Menu, X } from "lucide-react";
import type { EmployeeRole } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  laneItems,
  meetingsToDocument,
  todaysMeetings,
  unreadChatCount,
  useClinicQueues,
  type ClinicQueues,
} from "@/lib/clinic-queues";
import { trappedFocusIndex } from "@/lib/focus-trap";
import {
  sectionsForRole,
  type NavBadge as NavBadgeKey,
  type NavSection,
} from "@/lib/nav-sections";
import { activeItems } from "@/lib/lab-result-check";
import { Wordmark } from "./Wordmark";
import { UpcomingMeetingBanner } from "./meeting-calendar/UpcomingMeetingBanner";

export { isPathAllowedForRole } from "@/lib/nav-sections";

// TES-506 — the count behind each nav entry, from the shell's single shared
// fetch, so every badge in the sidebar and the Idag dashboard agree.
function badgeCount(badge: NavBadgeKey, q: ClinicQueues): number {
  // TES-1284 — a lab check waiting for its next time is not counted.
  if (typeof badge === "object")
    return activeItems(laneItems(q.workQueue, badge.lane)).length;
  switch (badge) {
    case "meetings":
      return (
        todaysMeetings(q.meetings).length +
        meetingsToDocument(q.meetings).length
      );
    case "chat":
      return unreadChatCount(q.chat);
    case "transferReviews":
      return q.transferReviews ?? 0;
    case "workQueue":
      return (
        q.workQueue?.lanes.reduce(
          (n, l) => n + activeItems(l.items).length,
          0,
        ) ?? 0
      );
    case "supportInbox":
      return q.supportInbox ?? 0;
  }
}

const ROLE_LABEL: Record<EmployeeRole, string> = {
  doctor: "Läkare",
  coach: "Coach",
  admin: "Administratör",
};

// TES-506 — the count behind a nav entry. Zero renders nothing: a row of "0"s
// down the sidebar is noise that trains the eye to skip the badges entirely,
// which is the opposite of what the walkthrough ("genomgången") asked for.
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-auto flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-white/15 px-xs text-[11px] font-medium text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

// Dark-navy full-height sidebar matching the product mockups: brand at the
// top, white nav with icons + section labels and a subtle active highlight,
// and a clinician card pinned to the bottom. Content takes the rest.
//
// TES-966 — one sidebar, rendered in two places: pinned to the left from `lg`
// up, and inside the phone drawer below it. `headerAction` is the drawer's
// close button; `onNavigate` closes the drawer even when the tapped entry is
// the page already open (no route change to react to).
function SidebarContent({
  sections,
  queues,
  onNavigate,
  headerAction,
}: {
  sections: NavSection[];
  queues: ClinicQueues;
  onNavigate?: () => void;
  headerAction?: ReactNode;
}) {
  const { signOut, claims } = useAuth();
  const role = claims?.role as EmployeeRole | undefined;
  // TES-1089 — a granted role is named too ("Coach · Administratör"), so the
  // shell says why the admin sections are there.
  const roleLabel = role
    ? [role, ...(claims?.roles ?? []).filter((r) => r !== role)]
        .filter((r): r is EmployeeRole => r in ROLE_LABEL)
        .map((r) => ROLE_LABEL[r])
        .join(" · ")
    : null;
  return (
    <>
      {headerAction ? (
        <div className="px-lg pt-lg pb-md flex items-center justify-between">
          <Wordmark size="sm" tone="light" />
          {headerAction}
        </div>
      ) : (
        <div className="px-lg pt-lg pb-md">
          <Wordmark size="sm" tone="light" />
        </div>
      )}
      <nav className="flex-1 px-sm flex flex-col gap-md overflow-y-auto">
        {sections.map((section, i) => (
          <div key={section.label ?? i}>
            {section.label ? (
              <p className="px-md pb-xs text-[11px] font-semibold uppercase tracking-wider text-white/35">
                {section.label}
              </p>
            ) : null}
            <ul className="flex flex-col gap-xs">
              {section.items
                .filter((item) => !item.hidden)
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          `flex items-center gap-sm px-md py-sm rounded-md text-sm transition ${
                            isActive
                              ? "bg-white/10 text-white font-medium"
                              : "text-white/65 hover:bg-white/5 hover:text-white"
                          }`
                        }
                      >
                        <Icon size={18} strokeWidth={1.75} />
                        {item.label}
                        {item.badge ? (
                          <NavBadge count={badgeCount(item.badge, queues)} />
                        ) : null}
                      </NavLink>
                    </li>
                  );
                })}
            </ul>
          </div>
        ))}
      </nav>
      {/* Name over role: every decision the portal records is attributed to
          this employee, so the portal has to be able to name them. Falls
          back to "Inloggad" when the API is older than this bundle or the
          row genuinely has no name. */}
      <div className="px-md py-md border-t border-white/10 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm text-white/90 truncate">
            {claims?.displayName?.trim() || "Inloggad"}
          </p>
          {roleLabel ? (
            <p className="text-xs text-white/45">{roleLabel}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-xs text-white/60 hover:text-white shrink-0"
        >
          Logga ut
        </button>
      </div>
    </>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

// TES-966 — below `lg` the sidebar took most of a phone's width. There it
// becomes an off-canvas drawer behind a menu button: modal while open (focus
// trapped, Esc and a backdrop tap close it), closed again by any navigation.
function NavDrawer({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    // overflow:hidden on <html> rather than a position:fixed body lock, which
    // on iOS Safari loses the scroll position and fights the URL bar.
    const html = document.documentElement;
    html.classList.add("overflow-hidden");
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      const next = trappedFocusIndex(
        items.indexOf(document.activeElement as HTMLElement),
        items.length,
        e.shiftKey,
      );
      if (next !== null) {
        e.preventDefault();
        items[next].focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    // Widening past `lg` hides the drawer; close it so the lock goes too.
    const wide = window.matchMedia("(min-width: 1024px)");
    const onWide = () => {
      if (wide.matches) onClose();
    };
    wide.addEventListener("change", onWide);
    return () => {
      wide.removeEventListener("change", onWide);
      document.removeEventListener("keydown", onKeyDown);
      html.classList.remove("overflow-hidden");
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panelRef}
        id="nav-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Meny"
        className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-brand-deep text-white flex flex-col shadow-xl"
      >
        {children}
      </div>
    </div>
  );
}

export function AppShell() {
  // TES-955 — the care meeting is the patient's "Vårdmöten" tab with a
  // meeting open; it needs the whole width (big video, note beside it).
  const location = useLocation();
  const fullWidth = isMeetingOnScreen(location.pathname, location.search);
  const { claims } = useAuth();
  const queues = useClinicQueues();
  const role = claims?.role as EmployeeRole | undefined;
  const sections = sectionsForRole(role, claims?.roles, claims?.permissions);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);
  // Back/forward and in-page links move the route without touching the nav.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background bg-page bg-fixed">
      <aside className="hidden lg:flex w-60 shrink-0 bg-brand-deep text-white flex-col">
        <SidebarContent sections={sections} queues={queues} />
      </aside>

      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-sm h-14 px-md bg-brand-deep text-white">
        <button
          ref={menuButtonRef}
          type="button"
          aria-label="Meny"
          aria-expanded={menuOpen}
          aria-controls="nav-drawer"
          onClick={() => setMenuOpen(true)}
          className="-ml-sm p-sm rounded-md hover:bg-white/10"
        >
          <Menu size={22} strokeWidth={1.75} />
        </button>
        <Wordmark size="sm" tone="light" />
      </header>
      <NavDrawer open={menuOpen} onClose={closeMenu}>
        <SidebarContent
          sections={sections}
          queues={queues}
          onNavigate={closeMenu}
          headerAction={
            <button
              type="button"
              aria-label="Stäng menyn"
              onClick={closeMenu}
              className="-mr-sm p-sm rounded-md text-white/80 hover:bg-white/10 hover:text-white"
            >
              <X size={20} strokeWidth={1.75} />
            </button>
          }
        />
      </NavDrawer>

      {/* From lg up the window scrolls, never <main>: an overflow-auto main
          that grows with its content never scrolls itself, yet it is the box
          `position: sticky` pins to, so the patient headers (TES-520,
          TES-1074) scrolled away. `clip` hides sideways overflow without
          making a scroll container. Below lg it stays as TES-966 left it. */}
      <main className="flex-1 min-w-0 p-md lg:p-lg overflow-auto lg:overflow-x-clip lg:overflow-y-visible">
        {/* The care meeting (a large video with the note beside it) needs the
            full width; the reading layouts keep their measure. */}
        <div className={fullWidth ? "" : "max-w-6xl mx-auto"}>
          {/* TES-1233 — the reminder before a meeting, on every page. */}
          <UpcomingMeetingBanner meetings={queues.meetings} />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
